import { addDays, format } from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import { useMemo, useState } from 'react';

import {
  CurrentEventQuery,
  Milestone,
} from '../../../../../apollo/__generated__/graphql';
import { useCreateTicketTransactionMutation } from '../../../../../transactions/hooks/useCreateTicketTransactionMutation';
import { useVoucherQuery } from '../../../../../transactions/hooks/useVoucherQuery';

export const useOnlinePaymentCard = (
  event: NonNullable<CurrentEventQuery['currentEvent']>,
  milestone: Milestone,
  payForUserId?: string,
) => {
  const [numberOfDays, setNumberOfDays] = useState<number>(
    milestone.prices.length - 1,
  );
  const [arrivalDay, setArrivalDay] = useState<number>(0);
  const [isNoAlcool, setIsNoAlcool] = useState<boolean>(false);
  const [voucherText, setVoucherText] = useState<string>('');
  const [isForceDialogVisible, setIsForceDialogVisible] =
    useState<boolean>(false);

  const { createTicketTransaction } = useCreateTicketTransactionMutation();

  const { data: voucherData, getVoucher } = useVoucherQuery();

  const onAlcoolChange = (value: string | null) => {
    if (value === null) return;
    setIsNoAlcool(value === 'no-alcool');
  };

  const activeVoucher = useMemo(
    () => voucherData?.voucher ?? null,
    [voucherData],
  );

  const onPayWithHelloAsso = async () => {
    try {
      await createTicketTransaction({
        eventId: event.id,
        isNoAlcool,
        voucherId: activeVoucher?.id ?? null,
        arrivalDay,
        numberOfDays: numberOfDays + 1,
        payForUserId,
      });
    } catch (error) {
      if ((error as Error).message === 'USER_ALREADY_PAYING') {
        setIsForceDialogVisible(true);
      }
    }
  };

  const totalPrice = useMemo(() => {
    let price = milestone.prices[numberOfDays].value;

    if (milestone.noAlcoholDiscount && isNoAlcool) {
      price -= milestone.noAlcoholDiscount;
    }
    if (activeVoucher) {
      price -= activeVoucher.amount;
    }

    return price;
  }, [isNoAlcool, milestone, activeVoucher, numberOfDays]);

  const onSubmitVoucher = async () => {
    getVoucher(voucherText, event.id);
  };

  const onCloseForcePaymentDialog = () => {
    setIsForceDialogVisible(false);
  };
  const onForcePayment = () => {
    setIsForceDialogVisible(false);
    createTicketTransaction({
      eventId: event.id,
      isNoAlcool,
      voucherId: activeVoucher?.id ?? null,
      force: true,
      arrivalDay,
      numberOfDays: numberOfDays + 1,
    });
  };

  const numberOfDaysOptions = useMemo(() => {
    return milestone.prices.map((price) => price.day).reverse();
  }, [milestone]);

  const onNumberOfDaysChange = (value: number | null) => {
    if (value === null) return;

    setNumberOfDays(value);
    if (value === milestone.prices.length - 1) {
      setArrivalDay(0);
    }
  };

  const onArrivalDayChange = (value: number | null) => {
    if (value === null) return;

    setArrivalDay(value);
  };

  const arrivalDayOptions = useMemo(
    () => new Array(event.numberOfDays).fill(0).map((_, i) => i),
    [event],
  );

  const getDayNameByDay = (day: number) => {
    const date = addDays(event.startAt, day);
    return format(date, 'eeee', { locale: fr });
  };

  return {
    onPayWithHelloAsso,
    isNoAlcool,
    totalPrice,
    onAlcoolChange,
    setVoucherText,
    onSubmitVoucher,
    activeVoucher,
    isForceDialogVisible,
    onCloseForcePaymentDialog,
    onForcePayment,
    numberOfDaysOptions,
    numberOfDays,
    onNumberOfDaysChange,
    arrivalDay,
    onArrivalDayChange,
    arrivalDayOptions,
    getDayNameByDay,
  };
};
